/**
 * Confirmed actions the host executes itself (contract §3.3, the Office pane). A write the person confirmed, such as a
 * reply draft, a table or a meeting, runs through Office.js or Microsoft Graph with the person's own token, not on the
 * server. POST /v1/action answers `{status:'client-executing', actionId, token, tool, input, targetApp:'office'}`. The
 * widget runs `clientActions.execute` once and posts `{actionId, token, ok, result?, error?}` to `clientActions.ackPath`.
 *  - Exactly once, across panes and reloads: before it runs anything, a page claims the action id in a ledger that every
 *    pane of the add-in shares. In IndexedDB the claim is one `add`, which the browser commits atomically (a second `add`
 *    of the same id fails), so two panes of one install (Outlook's pinned pane and a popped-out message, two workbooks)
 *    never both run it, and a reload never runs it again. Where IndexedDB cannot answer, a ledger in browser storage
 *    stands in: still exact within the page and across a reload, but across panes only best effort (a read, then a
 *    write), and that is reported. An action whose run was cut off (the pane closed mid-write) is never run again: its
 *    outcome is unknown, and the server's handling of a stuck execution settles it, rather than a second run doubling a
 *    write.
 *  - Where it runs: in the page that pressed Confirm, and nowhere else on its own. That page runs it from its confirm
 *    answer, or, should the answer be lost, from the inbox entry still `executing` for the Office target. An action a
 *    second approver released also runs there if that page is still open. Every other pane of the install only offers a
 *    released action ("Run it here" on its card): the person picks the document it belongs to, so a document-bound write
 *    (a range, a slide) never lands in whichever workbook happened to poll first. "Run it here" takes the same atomic
 *    claim and the same execute and acknowledgement path.
 *  - A confirm answer runs only when it is for exactly the card the person confirmed: the Office target, the same action
 *    id, tool and input. Any other client-executing answer is refused loudly (the action id and a reason, never the
 *    input), the page never runs that action, and its card shows an error rather than "Running in ...".
 *  - Recovery: while the inbox shows the action `executing` or `reconciliation-required` (gyro-svc takes the
 *    acknowledgement in both), an acknowledgement that has not landed is sent again.
 *  - A network failure, a 401, a 429 or a 5xx is retried after 1 s, 3 s and 9 s, then once per later inbox sighting, up
 *    to 8 sends in all. A 401 is the add-in's own office_auth_required while Office sign-in needs the person again; the
 *    acknowledgement is taken once they are back. Any other refusal is final: it is reported loudly, and the card says
 *    the outcome is unknown.
 *  - What is sent back is bounded: a result only when it is a JSON object or array of at most 32 KB, an error code of
 *    the closed-set shape (`office_write_failed` otherwise), and a message of at most 200 characters.
 */
import { jsonEqual } from './inboxPoll.js';
import { isServiceApiPath } from './paneHost.js';
/** The surface whose client-executing actions a pane host runs. */
export const CLIENT_ACTION_TARGET_APP = 'office';
export const CLIENT_ACTION_FAILURE_CODE = 'office_write_failed';
export const CLIENT_ACTION_RESULT_MAX_BYTES = 32 * 1024;
/** Waits before the 2nd, 3rd and 4th acknowledgement sends; later sends follow inbox sightings. */
export const CLIENT_ACTION_ACK_RETRY_MS = [1_000, 3_000, 9_000];
export const CLIENT_ACTION_MAX_ACK_SENDS = 8;
/** Claimed action ids are kept this long (and, in browser storage, at most this many, newest first). */
export const CLIENT_ACTION_LEDGER_TTL_MS = 7 * 24 * 60 * 60_000;
export const CLIENT_ACTION_LEDGER_MAX = 200;
/** The IndexedDB database every pane of the add-in shares for claims (one store, `claims`). */
export const CLIENT_ACTION_CLAIMS_DB = 'gyro-client-actions';
/** How long an IndexedDB claim may take before browser storage answers instead. */
export const CLIENT_ACTION_CLAIM_TIMEOUT_MS = 5_000;
const CLAIMS_STORE = 'claims';
/** How many actions a page remembers as confirmed here, offered here or withheld (the newest are kept). */
const CONFIRMED_MAX = 200;
const MAX_MESSAGE_CHARS = 200;
const MAX_TOKEN_CHARS = 4_096;
const ACTION_ID = /^[A-Za-z0-9._:-]{1,200}$/;
const TOOL_NAME = /^[A-Za-z0-9_.:-]{1,128}$/;
const ERROR_CODE = /^[a-z0-9_]{1,64}$/;
/**
 * A POST /v1/action answer read for a pane host: the run it hands the host, a refusal (a client-executing answer the
 * pane will not run, with a short reason that never quotes the input), or null for any other answer (another status, or
 * an action another named client runs). gyro-svc answers an Office action with its own stored tool and input, the same
 * ones the card shows, so anything else is not what the person confirmed.
 */
function readClientExecuting(response, action) {
    const answer = record(response);
    if (!answer || answer.status !== 'client-executing')
        return null;
    // The desktop's answer names no target: a missing one is never taken for the Office target.
    if (answer.targetApp === undefined)
        return { refusal: 'the answer names no target app' };
    if (answer.targetApp !== CLIENT_ACTION_TARGET_APP)
        return null;
    if (answer.actionId !== action.id || !ACTION_ID.test(action.id))
        return { refusal: 'the answer is not for the action you confirmed' };
    const token = validToken(answer.token);
    if (!token)
        return { refusal: 'the answer carries no execution token' };
    if (answer.tool !== action.tool || !TOOL_NAME.test(action.tool))
        return { refusal: 'the answer names another tool than the card you confirmed' };
    const input = record(answer.input);
    if (!input || !jsonEqual(input, action.input))
        return { refusal: 'the answer carries other parameters than the card you confirmed' };
    return { request: { actionId: action.id, token, tool: action.tool, input } };
}
/**
 * The host's run from a POST /v1/action answer: a client-executing answer for the Office target about exactly the
 * confirmed card (the same action id, tool and input, and an execution token). Null for anything else, including the
 * desktop's answer, which names no target.
 */
export function clientExecutingFromResponse(response, action) {
    const reading = readClientExecuting(response, action);
    return reading && 'request' in reading ? reading.request : null;
}
/**
 * Why a pane host will not run a client-executing answer (clientExecutingFromResponse refused it), or null when the
 * answer is not one it was asked to run. A pane host reports it loudly (clientActionRefusalMessage) and shows the card
 * in error; the desktop, which runs its actions in the native agent, never asks.
 */
export function clientExecutingRefusal(response, action) {
    const reading = readClientExecuting(response, action);
    return reading && 'refusal' in reading ? reading.refusal : null;
}
/** The loud line for a refused client-executing answer: the action id and the reason, never the input. */
export function clientActionRefusalMessage(actionId, reason) {
    return `[gyro] client action ${ACTION_ID.test(actionId) ? actionId : '(unrecognised id)'} was not run: ${reason}`;
}
/** The sighting in a requester-inbox entry for the Office target, executing or awaiting reconciliation; or null. */
export function clientExecutingFromInbox(action) {
    const status = action.status === 'executing' ? 'executing' : action.status === 'reconciliation-required' ? 'reconciliation-required' : null;
    if (!status || action.targetApp !== CLIENT_ACTION_TARGET_APP)
        return null;
    const token = validToken(action.token);
    const input = record(action.input);
    if (typeof action.id !== 'string' || !ACTION_ID.test(action.id) || !token || typeof action.tool !== 'string' || !TOOL_NAME.test(action.tool) || !input)
        return null;
    return { actionId: action.id, token, tool: action.tool, input, status, approved: action.requiresApproval === true };
}
/** The host's outcome bounded to what the acknowledgement carries. Never throws. */
export function boundClientActionOutcome(value) {
    const outcome = record(value);
    if (!outcome || typeof outcome.ok !== 'boolean')
        return failed('The action returned no outcome.');
    if (outcome.ok) {
        const result = outcome.result;
        if (!result || typeof result !== 'object')
            return { ok: true };
        // The write happened: a result too large to send (or to serialize) is left out, never turned into a failure.
        try {
            if (new TextEncoder().encode(JSON.stringify(result)).length <= CLIENT_ACTION_RESULT_MAX_BYTES)
                return { ok: true, result };
        }
        catch {
            /* not serializable: acknowledge without it */
        }
        return { ok: true };
    }
    const error = record(outcome.error);
    const code = error && typeof error.code === 'string' && ERROR_CODE.test(error.code) ? error.code : CLIENT_ACTION_FAILURE_CODE;
    return failed(error && typeof error.message === 'string' ? error.message : undefined, code);
}
export function clientActionLedgerKey(appKey, userKey) {
    return `${appKey}-gyro-client-actions-v1-${userKey}`;
}
/**
 * The ids of actions this browser claimed, in browser storage, read again on every check (another pane of the same
 * add-in may have claimed one). An in-memory copy covers a storage that is missing or throws (private mode, a sandboxed
 * frame): exactly once then holds within this page. Only action ids are stored, so sign-out leaves it in place: wiping
 * it would let a re-run double a write. Two panes can both pass the read before either write lands, so it is the
 * stand-in for the atomic IndexedDB ledger, never a replacement.
 */
export function storageClientActionLedger(storage, key, now = Date.now) {
    const memory = new Map();
    const live = (at) => now() - at < CLIENT_ACTION_LEDGER_TTL_MS;
    const merge = () => {
        try {
            const parsed = JSON.parse(storage()?.getItem(key) ?? '{}');
            const stored = record(parsed);
            if (!stored)
                return;
            for (const [id, at] of Object.entries(stored)) {
                if (ACTION_ID.test(id) && typeof at === 'number' && Number.isFinite(at) && live(at) && !memory.has(id))
                    memory.set(id, at);
            }
        }
        catch {
            /* unreadable storage: the in-memory copy still answers */
        }
    };
    const has = (actionId) => {
        merge();
        const at = memory.get(actionId);
        return at !== undefined && live(at);
    };
    const add = (actionId) => {
        merge();
        memory.set(actionId, now());
        const kept = [...memory].filter(([, at]) => live(at)).sort((left, right) => right[1] - left[1]).slice(0, CLIENT_ACTION_LEDGER_MAX);
        memory.clear();
        for (const [id, at] of kept)
            memory.set(id, at);
        try {
            storage()?.setItem(key, JSON.stringify(Object.fromEntries(kept)));
        }
        catch {
            /* storage full or blocked: the in-memory copy still answers */
        }
    };
    return {
        has,
        add,
        claim(actionId) {
            if (has(actionId))
                return false;
            add(actionId);
            return true;
        },
    };
}
/**
 * The ledger every pane of the add-in shares, in IndexedDB: a claim is one `add` of `[scope, actionId]`, which the
 * browser commits atomically across all the add-in's pages, and which fails with a ConstraintError when the id was
 * claimed before. Claims older than CLIENT_ACTION_LEDGER_TTL_MS are dropped when the database is opened. A successful
 * claim is copied to the browser-storage ledger, and a claim found there counts, so a pane that has to fall back still
 * sees what the others took.
 */
export function indexedDbClientActionLedger(options) {
    const now = options.now ?? Date.now;
    const timeoutMs = options.timeoutMs ?? CLIENT_ACTION_CLAIM_TIMEOUT_MS;
    const warn = options.warn ?? ((message) => console.warn(message));
    let connection;
    let warned = false;
    const drop = () => {
        const open = connection;
        connection = undefined;
        void open?.then((db) => db.close(), () => undefined);
    };
    return {
        async claim(actionId) {
            if (options.fallback.has(actionId))
                return false;
            try {
                const database = connection ?? (connection = openClaims(options.indexedDB, now() - CLIENT_ACTION_LEDGER_TTL_MS));
                const claimed = await withTimeout(database.then((db) => addClaim(db, [options.scope, actionId], now())), timeoutMs);
                if (claimed)
                    options.fallback.add(actionId);
                return claimed;
            }
            catch (error) {
                // Opened again at the next claim; a connection that answers late is closed.
                drop();
                if (!warned) {
                    warned = true;
                    warn(`[gyro] client actions: IndexedDB could not record a claim (${describe(error)}); browser storage records it instead, so two panes of this add-in could both run one action`);
                }
                return options.fallback.claim(actionId);
            }
        },
        close: drop,
    };
}
/** The ledger the widget uses in a browser: IndexedDB, with browser storage standing in where IndexedDB cannot answer. */
export function browserClientActionLedger(scope) {
    return indexedDbClientActionLedger({
        indexedDB: () => globalThis.indexedDB,
        scope,
        fallback: storageClientActionLedger(() => globalThis.localStorage, scope),
    });
}
function openClaims(indexedDB, staleBefore) {
    return new Promise((resolve, reject) => {
        const factory = indexedDB();
        if (!factory)
            throw new Error('IndexedDB is not available');
        const request = factory.open(CLIENT_ACTION_CLAIMS_DB, 1);
        request.onupgradeneeded = () => {
            if (!request.result.objectStoreNames.contains(CLAIMS_STORE))
                request.result.createObjectStore(CLAIMS_STORE, { keyPath: 'key' });
        };
        request.onsuccess = () => {
            const db = request.result;
            // Never hold up a newer version of this database that another page opens.
            db.onversionchange = () => db.close();
            pruneClaims(db, staleBefore);
            resolve(db);
        };
        request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
    });
}
/** One claim: an `add`, committed, or refused with a ConstraintError because the id was claimed before. */
function addClaim(db, key, at) {
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(CLAIMS_STORE, 'readwrite');
        const request = transaction.objectStore(CLAIMS_STORE).add({ key, at });
        transaction.oncomplete = () => resolve(true);
        // The failed add aborts the transaction: another pane, or this browser before, holds the claim.
        transaction.onabort = () => {
            if (request.error?.name === 'ConstraintError')
                resolve(false);
            else
                reject(transaction.error ?? request.error ?? new Error('IndexedDB claim aborted'));
        };
    });
}
/** Housekeeping when the database opens: claims older than the ledger's lifetime are dropped. Best effort. */
function pruneClaims(db, staleBefore) {
    try {
        const store = db.transaction(CLAIMS_STORE, 'readwrite').objectStore(CLAIMS_STORE);
        const all = store.getAll();
        all.onsuccess = () => {
            for (const entry of all.result) {
                const claim = record(entry);
                if (claim && Array.isArray(claim.key) && !(typeof claim.at === 'number' && claim.at >= staleBefore))
                    store.delete(claim.key);
            }
        };
    }
    catch {
        /* housekeeping only: a claim is decided by addClaim */
    }
}
function withTimeout(work, ms) {
    let timer;
    const timeout = new Promise((_resolve, reject) => {
        timer = setTimeout(() => reject(new Error(`no answer within ${ms} ms`)), ms);
    });
    return Promise.race([work, timeout]).finally(() => clearTimeout(timer));
}
function describe(error) {
    return error instanceof Error ? `${error.name}: ${error.message}`.slice(0, 160) : 'unknown error';
}
export class ClientActionRunner {
    options;
    entries = new Map();
    /** Actions this page pressed Confirm on, oldest first: the only ones an inbox sighting starts here on its own. */
    confirmed = new Set();
    /** Approver-released actions another page confirmed, oldest first: their cards offer "Run it here" (runHere). */
    offers = new Map();
    /** Actions this page will never run: their confirm answer was not for exactly the confirmed card (withhold). */
    withholding = new Set();
    disposed = false;
    setTimer;
    clearTimer;
    warn;
    info;
    constructor(options) {
        this.options = options;
        this.setTimer = options.setTimer ?? ((run, ms) => setTimeout(run, ms));
        this.clearTimer = options.clearTimer ?? ((handle) => clearTimeout(handle));
        this.warn = options.warn ?? ((message) => console.warn(message));
        this.info = options.info ?? ((message) => console.info(message));
    }
    /**
     * This page is about to confirm `actionId`: its inbox sighting starts the run here should the confirm answer be lost,
     * or once a second approver released it.
     */
    confirming(actionId) {
        remember(this.confirmed, actionId);
    }
    /**
     * This page will never run `actionId`: its confirm answer was not for exactly the card the person confirmed
     * (clientExecutingRefusal). Later inbox sightings neither start it here nor offer it; gyro-svc settles it once its
     * execution lease runs out unacknowledged.
     */
    withhold(actionId) {
        this.confirmed.delete(actionId);
        this.offers.delete(actionId);
        remember(this.withholding, actionId);
    }
    /** Whether this page withholds `actionId` (withhold): its card stays in error while the inbox still lists it executing. */
    withheld(actionId) {
        return this.withholding.has(actionId);
    }
    /**
     * Run `request` on the host, once: this page's confirm answer said client-executing. A repeat for an action whose
     * acknowledgement stalled sends it again. False when this page will not run it: no host executor, an ackPath that is
     * not a gyro-svc path, a disposed runner, an action this page withholds, or one it already took. True when this page
     * claims it; the claim then decides, across panes and reloads, whether it runs here (phaseOf says 'skipped' when it
     * does not).
     */
    start(request, action) {
        if (this.disposed || this.withholding.has(request.actionId))
            return false;
        const existing = this.entries.get(request.actionId);
        if (existing) {
            if (existing.phase === 'stalled' && existing.sends < CLIENT_ACTION_MAX_ACK_SENDS) {
                existing.phase = 'acking';
                void this.send(existing);
            }
            return false;
        }
        const actions = this.options.actions();
        if (!actions)
            return false;
        // A write whose outcome could never be acknowledged must not run at all.
        if (!isServiceApiPath(actions.ackPath)) {
            this.warn(`[gyro] client action ${request.tool} was not run: the host's ackPath is not a gyro-svc /v1 path`);
            return false;
        }
        const entry = { request, action, phase: 'claiming', sends: 0, ackPath: actions.ackPath };
        this.entries.set(request.actionId, entry);
        void this.claimAndRun(entry, actions);
        return true;
    }
    /**
     * An inbox entry for the Office target that gyro-svc still takes an acknowledgement for. An acknowledgement that has
     * not landed is sent again, while the entry is executing or awaiting reconciliation. A run starts on its own only in
     * the page that pressed Confirm (its confirm answer was lost, or a second approver released it), and only while the
     * entry is executing. Another pane of the install never runs it on its own: an approver-released action is offered
     * there instead (offered, runHere), and anything else is left alone. False when nothing new starts.
     */
    sighted(sighting, action) {
        const { actionId } = sighting;
        if (this.disposed || this.withholding.has(actionId))
            return false;
        // Taken here before: a stalled acknowledgement is sent again; it never runs twice.
        if (this.entries.has(actionId))
            return this.start(sighting, action);
        // gyro-svc stopped waiting for the outcome: it still takes an acknowledgement, but nothing new starts.
        if (sighting.status !== 'executing') {
            this.offers.delete(actionId);
            return false;
        }
        if (this.confirmed.has(actionId)) {
            this.offers.delete(actionId);
            return this.start(sighting, action);
        }
        if (sighting.approved) {
            this.offers.delete(actionId);
            this.offers.set(actionId, { sighting, action });
            if (this.offers.size > CONFIRMED_MAX)
                this.offers.delete(this.offers.keys().next().value);
        }
        return false;
    }
    /**
     * Whether the card of `actionId` offers "Run it here" in this page: a second approver released it, another page (or
     * this one before a reload) pressed Confirm, the inbox still shows it executing, and this page has not taken it.
     */
    offered(actionId) {
        return !this.disposed && this.offers.has(actionId) && !this.entries.has(actionId);
    }
    /**
     * The person chose "Run it here" on an offered action's card: this page takes it exactly as it takes a confirmed
     * action (the same atomic claim, execute and acknowledgement). False when it is not offered here; true when this page
     * claims it (phaseOf says 'skipped' when another pane already had).
     */
    runHere(actionId) {
        const offer = this.offers.get(actionId);
        if (!offer || this.disposed)
            return false;
        this.offers.delete(actionId);
        return this.start(offer.sighting, offer.action);
    }
    /** Where this page is with an action it took: for diagnostics and tests. */
    phaseOf(actionId) {
        return this.entries.get(actionId)?.phase;
    }
    /** Stop retrying and let go of the ledger; an execution already running still sends its acknowledgement once. */
    dispose() {
        this.disposed = true;
        for (const entry of this.entries.values()) {
            if (entry.timer !== undefined)
                this.clearTimer(entry.timer);
            entry.timer = undefined;
        }
        this.options.ledger?.close?.();
    }
    async claimAndRun(entry, actions) {
        const { actionId, tool } = entry.request;
        let claimed = true;
        if (this.options.ledger) {
            try {
                claimed = await this.options.ledger.claim(actionId);
            }
            catch (error) {
                // A claim that cannot be recorded cannot prove this is the only run: nothing runs, and gyro-svc settles it.
                entry.phase = 'skipped';
                this.warn(`[gyro] client action ${tool} was not run: its claim could not be recorded (${describe(error)})`);
                return;
            }
        }
        if (!claimed) {
            entry.phase = 'skipped';
            this.info(`[gyro] client action ${tool} was not run here: another pane of this add-in, or this browser before a reload, already took it`);
            return;
        }
        if (this.disposed) {
            // Claimed, so no other page runs it either: its outcome is left to gyro-svc's handling of a stuck execution.
            entry.phase = 'skipped';
            this.warn(`[gyro] client action ${tool} was not run: the widget closed before it could start`);
            return;
        }
        entry.phase = 'running';
        await this.execute(entry, actions);
    }
    async execute(entry, actions) {
        const { actionId, token, tool, input } = entry.request;
        try {
            // A copy, so the host cannot change what is acknowledged; started inside a promise, so a synchronous throw is a failure.
            entry.outcome = boundClientActionOutcome(await Promise.resolve().then(() => actions.execute({ actionId, token, tool, input })));
        }
        catch (error) {
            this.warn(`[gyro] client action ${tool} failed on the host (${error instanceof Error ? error.name : 'error'})`);
            entry.outcome = failed('The action could not run here.');
        }
        entry.phase = 'acking';
        await this.send(entry);
    }
    async send(entry) {
        const outcome = entry.outcome;
        const { actionId, token, tool } = entry.request;
        entry.sends += 1;
        const body = {
            actionId,
            token,
            ok: outcome.ok,
            ...(outcome.result ? { result: outcome.result } : {}),
            ...(outcome.error ? { error: outcome.error } : {}),
        };
        let status = 0;
        let answer;
        let failure = '';
        try {
            ({ status, body: answer } = await this.options.ack(entry.ackPath ?? '', body));
        }
        catch (error) {
            failure = error instanceof Error ? error.message : 'network error';
        }
        if (status >= 200 && status < 300) {
            entry.phase = 'acked';
            this.options.settled(entry.action, settledResponse(outcome, answer));
            return;
        }
        const code = record(answer)?.error;
        const reason = status ? `HTTP ${status}${typeof code === 'string' ? ` ${code.slice(0, 80)}` : ''}` : failure;
        // A 401 is retried like the network: the add-in answers office_auth_required itself while Office sign-in needs the
        // person again, and gyro-svc still takes the acknowledgement once they are back.
        if (status !== 0 && status !== 401 && status !== 429 && status < 500) {
            entry.phase = 'refused';
            this.warn(`[gyro] client action ${tool} ${outcome.ok ? 'ran' : 'failed'} on the host, but gyro-svc refused its acknowledgement (${reason})`);
            this.options.settled(entry.action, {
                status: 'reconciliation-required',
                reconciliationRequired: true,
                message: `The result could not be recorded (${reason}).`,
            });
            return;
        }
        const wait = CLIENT_ACTION_ACK_RETRY_MS[entry.sends - 1];
        if (wait !== undefined && entry.sends < CLIENT_ACTION_MAX_ACK_SENDS && !this.disposed) {
            entry.timer = this.setTimer(() => {
                entry.timer = undefined;
                void this.send(entry);
            }, wait);
            return;
        }
        entry.phase = 'stalled';
        this.warn(`[gyro] client action ${tool}: the acknowledgement has not reached gyro-svc after ${entry.sends} tries (${reason}); it is sent again while the inbox shows the action executing or awaiting reconciliation`);
    }
}
/** Add `id` as the newest of a bounded, oldest-first set. */
function remember(ids, id) {
    ids.delete(id);
    ids.add(id);
    if (ids.size > CONFIRMED_MAX)
        ids.delete(ids.values().next().value);
}
/**
 * Own a runner for one effect lifetime (the widget's useEffect): made at setup, published in `slot`, disposed at cleanup.
 * A runner made in a memo outlives the cleanup that disposed it: React's StrictMode runs every effect's cleanup once
 * right after mount in development (the add-in renders in StrictMode), Fast Refresh does it on every edit, and a
 * disposed runner never starts anything again, so each confirmed action would sit "Running in ..." for good.
 */
export function mountClientActionRunner(slot, create) {
    const runner = create();
    slot.current = runner;
    return () => {
        runner.dispose();
        if (slot.current === runner)
            slot.current = null;
    };
}
/** What the card records: the server's own answer when it gave a status, else the host's outcome as a /v1/action answer. */
function settledResponse(outcome, answer) {
    const server = record(answer);
    if (server && typeof server.status === 'string' && ['done', 'error', 'cancelled', 'reconciliation-required'].includes(server.status))
        return server;
    return outcome.ok
        ? { status: 'done', ...(outcome.result ? { result: outcome.result } : {}) }
        : { status: 'error', message: outcome.error?.message ?? `The action failed (${outcome.error?.code ?? CLIENT_ACTION_FAILURE_CODE}).` };
}
function failed(message, code = CLIENT_ACTION_FAILURE_CODE) {
    const text = message?.replace(/\s+/g, ' ').trim().slice(0, MAX_MESSAGE_CHARS);
    return { ok: false, error: text ? { code, message: text } : { code } };
}
function validToken(value) {
    return typeof value === 'string' && value.length > 0 && value.length <= MAX_TOKEN_CHARS ? value : null;
}
function record(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}
//# sourceMappingURL=clientActions.js.map